import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { contentApi, ScopeBadge, scopePath } from "./contentApi";
import ContentConfirm from "./ContentConfirm";
import { AdminListPager, AdminListToolbar } from "@/components/admin/AdminListToolbar";
import { useAdminListQuery } from "@/components/admin/useAdminListQuery";
import { base44 } from "@/api/base44Client";

const emptyForm = () => ({
  title: "",
  description: "",
  category: "",
  sector: "",
  geography: "",
  content: "",
  file_url: "",
  external_url: "",
});

export default function ResourceEditor({
  scope,
  onMessage,
  onDirtyChange,
  onBusyChange,
}) {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [refreshError, setRefreshError] = useState("");
  const [reconcilePending, setReconcilePending] = useState(false);
  const [deactivateRow, setDeactivateRow] = useState(null);
  const [checkingId, setCheckingId] = useState(null);
  const [linkCheckMessage, setLinkCheckMessage] = useState("");
  const [pendingFormDiscard, setPendingFormDiscard] = useState(null);
  const generation = useRef(0);
  const requestId = useRef(crypto.randomUUID());
  const busyRef = useRef(false);
  const abortRef = useRef(null);
  const [total, setTotal] = useState(0);
  const { query, update: updateQuery, reset: resetQuery } = useAdminListQuery();

  useEffect(() => onDirtyChange(dirty), [dirty, onDirtyChange]);
  useEffect(
    () => onBusyChange(busy || reconcilePending),
    [busy, reconcilePending, onBusyChange],
  );

  async function loadRows({ postCommit = false, reconcile = false } = {}) {
    const current = ++generation.current;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    if (postCommit || reconcile) setReconcilePending(true);
    else setReconcilePending(false);
    if (!postCommit) setError("");
    try {
      const result = await contentApi(
        `/admin/content/resources?organisation_id=${scopePath(scope)}&paged=true&limit=100${query.q ? `&q=${encodeURIComponent(query.q)}` : ""}${query.status ? `&status=${encodeURIComponent(query.status)}` : ""}${query.category ? `&category=${encodeURIComponent(query.category)}` : ""}${query.link_status ? `&link_status=${encodeURIComponent(query.link_status)}` : ""}${query.offset ? `&offset=${encodeURIComponent(query.offset)}` : ""}`,
        { signal: controller.signal },
      );
      if (current !== generation.current) return true;
      setRows(result.items || result);
      setTotal(Number(result.total) || (Array.isArray(result) ? result.length : 0));
      setRefreshError("");
      setReconcilePending(false);
      return true;
    } catch (e) {
      if (e.name === "AbortError" || current !== generation.current)
        return false;
      setRefreshError(
        postCommit ? `Saved, but refresh failed: ${e.message}` : e.message,
      );
      setReconcilePending(postCommit || reconcile);
      return false;
    } finally {
      if (current === generation.current) setLoading(false);
    }
  }

  useLayoutEffect(() => {
    setRows([]);
    setEditing(null);
    setForm(emptyForm());
    setDirty(false);
    setAttachment(null);
    setCheckingId(null);
    setLinkCheckMessage("");
    loadRows();
    return () => {
      generation.current += 1;
      abortRef.current?.abort();
    };
  }, [scope, query.q, query.status, query.category, query.link_status, query.offset]);

  function update(key, value) {
    setForm((old) => ({ ...old, [key]: value }));
    setDirty(true);
  }

  function reset() {
    requestId.current = crypto.randomUUID();
    setForm(emptyForm());
    setEditing(null);
    setDirty(false);
    setAttachment(null);
  }

  function edit(row) {
    setAttachment(null);
    setEditing(row);
    setForm({
      title: row.title,
      description: row.description || "",
      category: row.category,
      sector: row.sector || "",
      geography: row.geography || "",
      content: row.content || "",
      file_url: row.file_url || "",
      external_url: row.external_url || "",
    });
    setDirty(false);
  }

  function requestEdit(row) {
    if (dirty) setPendingFormDiscard({ row });
    else edit(row);
  }

  function requestReset() {
    if (dirty) setPendingFormDiscard({ reset: true });
    else reset();
  }

  function confirmFormDiscard() {
    const pending = pendingFormDiscard;
    setPendingFormDiscard(null);
    if (pending?.reset) reset();
    else if (pending?.row) edit(pending.row);
  }

  async function save(event) {
    event.preventDefault();
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setError("");
    const requestScope = scope;
    const requestGeneration = generation.current;
    const editingId = editing?.id;
    const requestBody = {
      ...form,
      organisation_id: requestScope === "global" ? null : requestScope,
    };
    try {
      const row = !editingId && attachment && requestScope === "global"
        ? await base44.integrations.Core.UploadResource({ file: attachment, ...form, global: true, request_id: requestId.current })
        : editingId
        ? await contentApi(
            `/admin/content/resources/${editingId}?organisation_id=${scopePath(requestScope)}`,
            {
              method: "PATCH",
              body: (({ organisation_id: _ignored, ...patch }) => patch)(
                requestBody,
              ),
            },
          )
        : await contentApi("/admin/content/resources", {
            method: "POST",
            body: requestBody,
          });
      if (requestGeneration !== generation.current || requestScope !== scope)
        return;
      setRows((old) =>
        editingId
          ? old.map((item) => (item.id === row.id ? row : item))
          : [row, ...old],
      );
      reset();
      onMessage(editingId ? "Resource updated" : "Resource added");
      await loadRows({ postCommit: true });
    } catch (e) {
      if (requestGeneration === generation.current) setError(e.message);
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  }

  async function deactivate() {
    const row = deactivateRow;
    setDeactivateRow(null);
    if (!row || busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setError("");
    const requestGeneration = generation.current;
    try {
      const result = await contentApi(
        `/admin/content/resources/${row.id}?organisation_id=${scopePath(scope)}`,
        { method: "DELETE" },
      );
      if (requestGeneration !== generation.current) return;
      setRows((old) =>
        old.map((item) =>
          item.id === row.id
            ? result?.id
              ? result
              : { ...item, is_active: false }
            : item,
        ),
      );
      onMessage("Resource deactivated");
      await loadRows({ postCommit: true });
    } catch (e) {
      if (requestGeneration === generation.current) setError(e.message);
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  }

  async function checkLink(row) {
    if (checkingId) return;
    const current = generation.current;
    setCheckingId(row.id);
    setError("");
    setLinkCheckMessage(`Checking ${row.title}…`);
    try {
      const updated = await contentApi(`/admin/content/resources/${row.id}/check-link`, { method: "POST" });
      if (current !== generation.current) return;
      setRows((existing) => existing.map((item) => item.id === updated.id ? updated : item));
      const label = updated.link_health_status?.replaceAll("_", " ") || "unchecked";
      setLinkCheckMessage(`${row.title}: link check complete, ${label}.`);
    } catch (e) {
      if (current !== generation.current) return;
      setError(e.message || "The link could not be checked. Try again.");
      setLinkCheckMessage(`${row.title}: link check failed.`);
    } finally {
      if (current === generation.current) setCheckingId(null);
    }
  }

  function healthLabel(row) {
    return row.link_health_status?.replaceAll("_", " ") || "unchecked";
  }

  const field = (key, label, type = "input") => (
    <label key={key}>
      {label}
      {type === "textarea" ? (
        <textarea
          maxLength={key === "content" ? 50000 : 10000}
          value={form[key]}
          onChange={(e) => update(key, e.target.value)}
        />
      ) : (
        <input
          maxLength={key === "title" ? 300 : 2000}
          value={form[key]}
          onChange={(e) => update(key, e.target.value)}
        />
      )}
    </label>
  );

  return (
    <section
      className="content-editor-section"
      aria-labelledby="resource-editor-title"
    >
      <div className="content-section-heading">
        <div>
          <h2 id="resource-editor-title">Resource bank</h2>
          <p>Publish safe links and guidance for the selected scope.</p>
        </div>
        <button
          type="button"
          className="content-button secondary"
          onClick={() => loadRows({ reconcile: reconcilePending })}
          disabled={busy || loading || reconcilePending}
        >
          Refresh
        </button>
      </div>
      <AdminListToolbar search={query.q || ""} searchLabel="Search resource title, description, or category" total={total} loading={loading} onSearch={(q) => updateQuery({ q })} onReset={resetQuery}>
        <label className="text-sm font-medium">Status<select aria-label="Filter resource status" value={query.status || "all"} onChange={(event) => updateQuery({ status: event.target.value })} className="mt-1 block min-h-11 rounded-lg border border-input bg-background px-3 py-2 text-sm"><option value="all">All statuses</option><option value="active">Active</option><option value="inactive">Inactive</option></select></label>
        <label className="text-sm font-medium">Link health<select aria-label="Filter link health" value={query.link_status || "all"} onChange={(event) => updateQuery({ link_status: event.target.value })} className="mt-1 block min-h-11 rounded-lg border border-input bg-background px-3 py-2 text-sm"><option value="all">All link statuses</option><option value="unchecked">Unchecked</option><option value="healthy">Healthy</option><option value="redirected">Redirected</option><option value="missing">Missing</option><option value="temporary_failure">Temporary failure</option><option value="blocked">Blocked</option></select></label>
        <label className="text-sm font-medium">Category<input aria-label="Filter resource category" value={query.category || ""} onChange={(event) => updateQuery({ category: event.target.value })} className="mt-1 block min-h-11 rounded-lg border border-input bg-background px-3 py-2 text-sm" /></label>
      </AdminListToolbar>
      <p className="sr-only" role="status" aria-live="polite">{linkCheckMessage}</p>
      {error && (
        <p className="content-error" role="alert">
          {error}
        </p>
      )}
      {refreshError && (
        <p className="content-error" role="alert">
          {refreshError}{" "}
          <button
            type="button"
            className="content-button secondary"
            onClick={() => loadRows({ reconcile: true })}
          >
            Retry refresh
          </button>
        </p>
      )}
      <form className="content-card content-form" onSubmit={save}>
        <fieldset disabled={busy || loading || reconcilePending}>
          <legend>{editing ? "Edit resource" : "Add resource"}</legend>
          <div className="content-form-grid">
            {field("title", "Title")}
            {field("category", "Category")}
            {field("sector", "Sector")}
            {field("geography", "Geography")}
          </div>
          {field("description", "Description", "textarea")}
          {field("content", "Content", "textarea")}
          <div className="content-form-grid">
            {!editing && scope === "global" ? <label>Attachment (optional)<input type="file" onChange={(event) => { setAttachment(event.target.files?.[0] || null); setDirty(true); }} />{attachment && <span className="content-muted">Selected: {attachment.name}</span>}</label> : field("file_url", "File URL")}
            {field("external_url", "External URL")}
          </div>
          <div className="content-actions">
            <button
              className="content-button primary"
              disabled={busy || loading}
            >
              {busy ? "Saving…" : editing ? "Save changes" : "Add resource"}
            </button>
            {editing && (
              <button
                type="button"
                className="content-button secondary"
                onClick={requestReset}
                disabled={busy || reconcilePending}
              >
                Cancel
              </button>
            )}
          </div>
        </fieldset>
      </form>
      <div className="content-card">
        <h3>Resources ({rows.length})</h3>
        {rows.length === 0 ? (
          <p className="content-muted">
            {loading ? "Loading resources…" : "No resources in this scope."}
          </p>
        ) : (
          <div className="content-row-list">
            {rows.map((row) => (
              <article
                className={`content-row ${row.is_active ? "" : "inactive"}`}
                key={row.id}
              >
                <div className="content-row-main">
                  <div className="content-row-title">
                    <strong>{row.title}</strong>
                    <ScopeBadge row={row} />
                  </div>
                  <p>
                    {row.category}
                    {row.description ? ` · ${row.description}` : ""}
                  </p>
                  {!row.is_active && (
                    <span className="content-status">Inactive</span>
                  )}
                  {row.external_url && <p className="content-muted">
                    Link health: <strong>{healthLabel(row)}</strong>
                    {row.link_health_http_status ? ` · HTTP ${row.link_health_http_status}` : ""}
                    {row.link_health_checked_at ? ` · checked ${new Date(row.link_health_checked_at).toLocaleString()}` : " · not checked yet"}
                    {row.link_health_final_url && row.link_health_final_url !== row.external_url ? ` · final destination ${row.link_health_final_url}` : ""}
                    {row.link_health_detail ? ` · ${row.link_health_detail}` : ""}
                  </p>}
                </div>
                <div className="content-actions">
                  <button
                    type="button"
                    className="content-button secondary"
                    onClick={() => requestEdit(row)}
                    disabled={busy || reconcilePending || !row.is_active}
                  >
                    Edit
                  </button>
                  {row.external_url && <button
                    type="button"
                    className="content-button secondary"
                    onClick={() => checkLink(row)}
                    disabled={busy || reconcilePending || Boolean(checkingId)}
                  >
                    {checkingId === row.id ? "Checking…" : "Check link"}
                  </button>}
                  {row.is_active && (
                    <button
                      type="button"
                      className="content-button danger"
                      onClick={() => setDeactivateRow(row)}
                      disabled={busy || reconcilePending}
                    >
                      Deactivate
                    </button>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
      <AdminListPager offset={Number(query.offset || 0)} limit={100} total={total} disabled={loading || Boolean(busy)} onPage={(offset) => updateQuery({ offset }, { resetOffset: false })} />
      <ContentConfirm
        open={Boolean(deactivateRow)}
        title="Deactivate this resource?"
        description="The resource remains in history and can be audited."
        action="Deactivate"
        onConfirm={deactivate}
        onClose={() => setDeactivateRow(null)}
      />
      <ContentConfirm
        open={Boolean(pendingFormDiscard)}
        title="Discard resource changes?"
        description="Opening another resource or cancelling will discard the current form."
        action="Discard changes"
        onConfirm={confirmFormDiscard}
        onClose={() => setPendingFormDiscard(null)}
      />
    </section>
  );
}
